import { Link } from 'react-router-dom';
import { useSEO } from '../utils/seo';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';

export default function NotFound() {
  useSEO({
    title: '404 - Page Not Found | Roseville.org',
    description: 'The page you are looking for could not be found. Explore our directory, events, deals, and local resources for Roseville, California.',
    keywords: 'page not found, 404 error, Roseville directory, Roseville events, Roseville resources',
    canonical: '/404',
    noIndex: true,
  });

  const popularPages = [
    {
      name: 'Home',
      href: '/',
      icon: 'ri-home-line',
      description: 'Return to homepage'
    },
    {
      name: 'Business Directory',
      href: '/directory',
      icon: 'ri-building-line',
      description: 'Find local businesses'
    },
    {
      name: 'Events Calendar',
      href: '/calendar',
      icon: 'ri-calendar-event-line',
      description: 'Discover upcoming events'
    },
    {
      name: 'Local Deals',
      href: '/deals',
      icon: 'ri-price-tag-3-line',
      description: 'Browse exclusive offers'
    },
    {
      name: 'Parks & Recreation',
      href: '/parks',
      icon: 'ri-tree-line',
      description: 'Explore parks and trails'
    },
    {
      name: 'Local News',
      href: '/news',
      icon: 'ri-newspaper-line',
      description: 'Read latest news'
    },
    {
      name: 'Food & Drink',
      href: '/food-drink',
      icon: 'ri-restaurant-line',
      description: 'Find restaurants'
    },
    {
      name: 'Blog',
      href: '/blog',
      icon: 'ri-article-line',
      description: 'Read our articles'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-teal-50 via-white to-teal-50 py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative inline-block mb-6">
              <h1 className="text-9xl md:text-[12rem] font-black text-teal-100 select-none">
                404
              </h1>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-error-warning-line text-6xl md:text-7xl text-teal-600"></i>
              </div>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Oops! Page Not Found
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              The page you're looking for doesn't exist or has been moved. Let's get you back on track with these helpful links.
            </p>

            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line text-xl"></i>
              Back to Home
            </Link>
          </div>
        </div>

        {/* Popular Pages Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Popular Pages
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="group bg-white rounded-lg border border-gray-200 p-6 hover:border-teal-600 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                  <i className={`${page.icon} text-2xl`}></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {page.name}
                </h4>
                <p className="text-sm text-gray-600">
                  {page.description}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white border-t border-gray-200 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Still can't find what you're looking for?
            </h3>
            <p className="text-gray-600 mb-6">
              Try searching our directory or contact us for assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/directory"
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-600 border-2 border-teal-600 px-6 py-3 rounded-lg font-medium hover:bg-teal-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line text-xl"></i>
                Search Directory
              </Link>
              <a
                href="mailto:info@roseville.org"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}